/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { DataTable, DataTableHead, DataTableHeader, Tooltip } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@stoneage/App/Components/Notify";
import BomTableProductCard from "@stoneage/App/Components/ProductDetails/BomTableProductCard";
import PointData from "@stoneage/App/Components/ProductDetails/PointData";
import getProductBom from "@stoneage/App/Handlers/Pages/ProductDetails/GetProductBom";
import { CustomAppState, Kit } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaBomNumberEvent,
    trackGaKitFilterEvent,
} from "@stoneage/App/Utilities/DataLayer";
import Clickable from "@stoneage/MobiusOverrides/Clickable";
import LazyImage from "@stoneage/MobiusOverrides/LazyImage";
import IconBackTop from "@stoneage/XDL/Icons/IconBackTop";
import { xClassNames } from "@stoneage/XDL/Utilities/classNames";
import Tippy from "@tippyjs/react";
import isEqual from "lodash/isEqual";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

// eslint-disable-next-line spire/fenced-imports
import { inlinePositioning } from "tippy.js";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface Coords {
    xPos: number;
    yPos: number;
    xPercentage: any;
    yPercentage: any;
    productId: string;
}

interface Size {
    bottom?: number;
    height?: number;
    left?: number;
    right?: number;
    top?: number;
    width?: number;
    x?: number;
    y?: number;
}

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => {
    const mainProductNumber = ownProps?.product?.productNumber;
    const productBom = state.xProductBom[mainProductNumber]?.data;
    return {
        productBom,
        productsInfo: state.xProductBom[mainProductNumber]?.productsInfo,
        isLoading: state.xProductBom[mainProductNumber]?.isLoading,
        bomImageName: (getSettingsCollection(state) as any)?.customSettings?.billOfMaterialImageName,
        pointsProducts: state.xProductBom[mainProductNumber]?.products,
        sortOrders: state.xProductBom[mainProductNumber]?.sortOrders,
        mainProductNumber,
        totalQuantity: state.xProductBomAction.totalQuantity,
        totalCost: state.xProductBomAction.totalCost,
        isAuthenticated:
            (state.context.session.isAuthenticated || state.context.session.rememberMe) &&
            !state.context.session.isGuest,
        currencySymbol: getSession(state).currency?.currencySymbol,
        checkedProducts: state.xProductBomAction.checkedProducts,
        contactForQuoteWidgetFields: getWidgetsByPageId(state, getCurrentPage(state).id).find((widget: WidgetProps) => {
            return widget.type === "ProductDetails/ContactUsForQuoteButton";
        })?.fields,
        kits: productBom?.billOfMaterialImages?.[0]?.kits,
        productsFromBomApi: productBom?.billOfMaterialImages?.[0]?.products,
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
        isMobileScreen: state.xCommon.isMobileScreen,
        kitFilterEvent: (state as CustomAppState).xGoogleAnalytics.kitFilterEvent,
        bomNumberEvent: (state as CustomAppState).xGoogleAnalytics.bomNumberEvent,
    };
};

const mapDispatchToProps = {
    getProductBom,
};
export interface PdpBomViewStyles {
    container?: InjectableCss;
    tippyPinWrapper?: InjectableCss;
    pointWrapper?: InjectableCss;
    point?: InjectableCss;
    hiddenPoint?: InjectableCss;
    imageWrapper?: InjectableCss;
    backLink?: LinkPresentationProps;
    tooltip?: TooltipPresentationProps;
    dataTable?: DataTablePresentationProps;
    kitFilterWrapper?: InjectableCss;
    kitFilter?: RadioProps;
    kitFilters?: RadioGroupProps;
    filterSectionTitle?: TypographyPresentationProps;
    filterName?: TypographyPresentationProps;
    productTitle?: TypographyPresentationProps;
    productPartNumber?: TypographyPresentationProps;
    bottomSection?: InjectableCss;
    smallHeadingAndTextStyle?: SmallHeadingAndTextStyles;
    selectPiece?: TypographyPresentationProps;
    IconInfoWrapper?: InjectableCss;
    InfoWrapper?: InjectableCss;
    backToTop?: ClickablePresentationProps;
    tableWrapper?: InjectableCss;
    completeTableWrapper?: InjectableCss;
    confirmModal?: TwoButtonModalStyles;
    bottomSectionInnerWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
}

export const pdpBomViewStyles: PdpBomViewStyles = {};

const styles = pdpBomViewStyles;

const TableHeading = () => {
    return (
        <DataTableHead>
            <DataTableHeader>{translate("#")}</DataTableHeader>
            <DataTableHeader>{translate("Part ID")}</DataTableHeader>
            <DataTableHeader>{translate("Description")}</DataTableHeader>
            <DataTableHeader>{translate("Inventory")}</DataTableHeader>
            <DataTableHeader>{translate("MSRP")}</DataTableHeader>
            <DataTableHeader>{translate("Disc. %")}</DataTableHeader>
            <DataTableHeader>{translate("Cost")}</DataTableHeader>
            <DataTableHeader>{translate("QTY")}</DataTableHeader>
            <DataTableHeader title="checkbox">{translate("Add")}</DataTableHeader>
        </DataTableHead>
    );
};

const ProductDetailsBomView = ({
    product,
    productBom,
    isLoading,
    getProductBom,
    bomImageName,
    pointsProducts,
    productsInfo,
    sortOrders,
    mainProductNumber,
    totalQuantity,
    totalCost,
    isAuthenticated,
    currencySymbol,
    checkedProducts,
    contactForQuoteWidgetFields,
    kits,
    productsFromBomApi,
    customerNumber,
    impersonation,
    isMobileScreen,
    kitFilterEvent,
    bomNumberEvent,
}: Props) => {
    const dispatch = useDispatch();
    const [selectedProduct, setSelectedProduct] = useState<Coords>({} as Coords);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [productImage, setProductImage] = useState<ImageModel | undefined>(undefined);
    const [selectedKit, setSelectedKit] = useState<string>("all"); // has the name of selected kit (will currnetly store product number until we have the names)
    const [selectedKitObject, setSelectedKitObject] = useState<Kit | undefined>(undefined);
    const [temprarySelectedKit, setTemprarySelectedKit] = useState<string>("all");
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = React.useState(false);
    const [size, setSize] = useState<Size>({});
    const [isSticky, setIsSticky] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const updateSize = useCallback(() => {
        if (wrapperRef.current) {
            setSize(wrapperRef.current.getBoundingClientRect());
        }
    }, []);

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            setIsSticky(!entry.isIntersecting);
        });
    });

    useEffect(() => {
        updateSize();
        window.addEventListener("resize", updateSize);

        if (!wrapperRef.current) {
            return;
        }

        // Observe the sticky element
        observer.observe(wrapperRef.current);

        return () => {
            window.removeEventListener("resize", updateSize);
            observer.disconnect();
        };
    }, [wrapperRef?.current]);

    React.useEffect(() => {
        // clear checked products
        dispatch({ type: "xProductBomAction/reset" });

        if (!productBom) {
            getProductBom({ productNumber: product.productNumber });
        }
    }, []);

    React.useEffect(() => {
        const image = new Image();
        const img = product?.images?.filter(img => img.name === bomImageName)?.[0];
        setProductImage(img);
        image.src = img?.largeImagePath || img?.mediumImagePath || ""; // Set the source of the image

        image.onload = () => {
            // Get the image size when the image is loaded
            setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
        };
    }, [product?.images]);

    React.useEffect(() => {
        if (selectedKit === "all") {
            setSelectedKitObject(undefined);
        }
    }, [selectedKit, pointsProducts]);

    if (!product || !productBom) {
        return null;
    }

    const goBackHandler = () => {
        const url = new URL(window.location.href);

        // Remove the specific query parameter
        url.searchParams.delete("showbom");

        // Update the URL without causing a page refresh
        window.history.replaceState({}, document.title, url.toString());
        dispatch({ type: "xCommon/update", data: { key: "isBomView", value: false } });
    };

    const resetTempQuantities = () => {
        dispatch({ type: "xProductBom/ResetQuantities", key: mainProductNumber });
    };

    const kitChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        // reset checked products
        if (checkedProducts && Object.keys(checkedProducts).length) {
            setTemprarySelectedKit(event.currentTarget.value);
            setConfirmationModalIsOpen(true);
        } else {
            kitFilterEvent &&
                trackGaKitFilterEvent(
                    mainProductNumber,
                    pointsProducts[event.currentTarget.value]?.productTitle || event.currentTarget.value,
                    impersonation,
                    customerNumber,
                );
            setSelectedKit(event.currentTarget.value);
            setSelectedKitObject(kits.find(kit => kit.productNumber === event.currentTarget.value));
            resetTempQuantities();
        }
    };
    const cancelClickHandler = () => {
        setTemprarySelectedKit("all");
        setConfirmationModalIsOpen(false);
    };
    const confirmClickHandler = () => {
        kitFilterEvent &&
            trackGaKitFilterEvent(
                mainProductNumber,
                pointsProducts[temprarySelectedKit]?.productTitle || temprarySelectedKit,
                impersonation,
                customerNumber,
            );
        setSelectedKit(temprarySelectedKit);
        setSelectedKitObject(kits.find(kit => kit.productNumber === temprarySelectedKit));

        setTemprarySelectedKit("all");
        setConfirmationModalIsOpen(false);
        dispatch({ type: "xProductBomAction/reset" });
        resetTempQuantities();
    };

    const backToTopClickHandler = () => {
        const bomImage = document.getElementById("bomImage");
        if (bomImage) {
            window.scrollTo({
                top: bomImage.offsetTop,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.container}>
                <Link {...styles.backLink} onClick={goBackHandler}>
                    {translate("Go Back")}
                </Link>
                <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                <Typography {...styles.productPartNumber}>{product.productNumber}</Typography>
                <StyledWrapper {...styles.imageWrapper} id="bomImage">
                    {productImage && (
                        <LazyImage
                            disableLazyLoad={true}
                            src={productImage.largeImagePath || productImage.mediumImagePath}
                        />
                    )}
                    {imageSize.width &&
                        imageSize.height &&
                        productsFromBomApi?.map(point => {
                            const productId = pointsProducts[point.productNumber]?.id;
                            const pointProduct = pointsProducts[point.productNumber];
                            const pointIsInSelectedKit =
                                selectedKit === "all"
                                    ? true
                                    : selectedKitObject?.kitProducts?.some(
                                          product => product.productNumber === point.productNumber,
                                      );
                            // a product might exist in many locations
                            return point.coordinates?.map(coordinate => {
                                const xCoord = coordinate.split(",")[0];
                                const yCoord = coordinate.split(",")[1];
                                // handle points without coordinates
                                if (xCoord === "" && yCoord === "") {
                                    return null;
                                }
                                const xPos = Number(xCoord);
                                const yPos = Number(yCoord);
                                const xPercentage = (xPos / imageSize.width) * 100;
                                const yPercentage = (yPos / imageSize.height) * 100;
                                const coord: Coords = { xPos, yPos, xPercentage, yPercentage, productId };
                                const productInfo = pointProduct?.productNumber
                                    ? { ...productsInfo[pointProduct?.productNumber || ""], qtyOrdered: point.qty }
                                    : {};

                                // We don't want points to be clickable on mobile or if it doesn't belong to the selected filter
                                if (!pointIsInSelectedKit || isMobileScreen) {
                                    return (
                                        <StyledWrapper
                                            {...({
                                                yPercentage: coord.yPercentage,
                                                xPercentage: coord.xPercentage,
                                            } as any)}
                                            {...styles.pointWrapper}
                                            key={`${xPos}_${yPos}`}
                                        >
                                            <StyledWrapper
                                                {...styles.hiddenPoint}
                                                {...({
                                                    yPercentage: coord.yPercentage,
                                                    xPercentage: coord.xPercentage,
                                                } as any)}
                                            />
                                            <StyledWrapper
                                                {...styles.point}
                                                className={xClassNames.append([
                                                    isEqual(selectedProduct, coord) ? "selected" : "",
                                                    pointIsInSelectedKit ? "is-in-filteres-group" : "",
                                                ])}
                                                {...({
                                                    yPercentage: coord.yPercentage,
                                                    xPercentage: coord.xPercentage,
                                                } as any)}
                                            >
                                                {point.sortOrder}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    );
                                }

                                return (
                                    <StyledWrapper {...styles.tippyPinWrapper} key={`${xPos}_${yPos}`}>
                                        <Tippy
                                            arrow={true}
                                            inlinePositioning={true}
                                            plugins={[inlinePositioning]}
                                            interactive={true}
                                            trigger="click"
                                            placement="auto"
                                            onHide={() => setSelectedProduct({} as Coords)}
                                            onMount={() =>
                                                setSelectedProduct({ xPos, yPos, xPercentage, yPercentage, productId })
                                            }
                                            onShow={() =>
                                                bomNumberEvent &&
                                                trackGaBomNumberEvent(
                                                    point.productNumber,
                                                    impersonation,
                                                    customerNumber,
                                                )
                                            }
                                            offset={[0, 25]}
                                            content={
                                                <PointData
                                                    productInfo={productInfo}
                                                    product={pointProduct}
                                                    point={point}
                                                    mainProductNumber={mainProductNumber}
                                                />
                                            }
                                            popperOptions={{
                                                modifiers: [
                                                    {
                                                        name: "flip",
                                                        options: {
                                                            fallbackPlacements: ["top", "right"],
                                                        },
                                                    },
                                                ],
                                            }}
                                        >
                                            <StyledWrapper
                                                {...({
                                                    yPercentage: coord.yPercentage,
                                                    xPercentage: coord.xPercentage,
                                                } as any)}
                                                {...styles.pointWrapper}
                                            >
                                                <StyledWrapper
                                                    {...styles.hiddenPoint}
                                                    {...({
                                                        yPercentage: coord.yPercentage,
                                                        xPercentage: coord.xPercentage,
                                                    } as any)}
                                                />
                                                <StyledWrapper
                                                    {...styles.point}
                                                    className={xClassNames.append([
                                                        isEqual(selectedProduct, coord) ? "selected" : "",
                                                        pointIsInSelectedKit ? "is-in-filteres-group" : "",
                                                    ])}
                                                    {...({
                                                        yPercentage: coord.yPercentage,
                                                        xPercentage: coord.xPercentage,
                                                    } as any)}
                                                >
                                                    {point.sortOrder}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </Tippy>
                                    </StyledWrapper>
                                );
                            });
                        })}
                </StyledWrapper>
                {kits && (
                    <>
                        <StyledWrapper>
                            {kits.length > 0 && (
                                <>
                                    <Typography {...styles.filterSectionTitle}>{translate("Filters")}</Typography>
                                    <RadioGroup
                                        {...styles.kitFilters}
                                        label=""
                                        value={selectedKit}
                                        onChangeHandler={kitChangeHandler}
                                    >
                                        <StyledWrapper
                                            {...styles.kitFilterWrapper}
                                            className={selectedKit === "all" ? "isSelected" : ""}
                                        >
                                            <Radio {...styles.kitFilter} value="all">
                                                {translate("All Parts")}
                                            </Radio>
                                            <Tooltip
                                                {...styles.tooltip}
                                                text={siteMessage("Custom_All_Parts_Tooltip_Message").toString()}
                                            />
                                        </StyledWrapper>

                                        {kits.map((kit: any, index: number) => {
                                            return (
                                                <StyledWrapper
                                                    {...styles.kitFilterWrapper}
                                                    key={index}
                                                    className={selectedKit === kit.productNumber ? "isSelected" : ""}
                                                >
                                                    <Radio {...styles.kitFilter} value={kit.productNumber}>
                                                        {/* Use product title */}
                                                        {pointsProducts[kit.productNumber]?.productTitle ||
                                                            kit.productNumber}
                                                    </Radio>
                                                    {kit.additionalInfoMessage && (
                                                        <Tooltip {...styles.tooltip} text={kit.additionalInfoMessage} />
                                                    )}
                                                </StyledWrapper>
                                            );
                                        })}
                                    </RadioGroup>
                                </>
                            )}

                            {/* display complete kit table */}
                            {selectedKit !== "all" && (
                                <>
                                    <Typography {...styles.filterName}>{translate("Complete Kit")}</Typography>
                                    {selectedKitObject &&
                                        (() => {
                                            const productInfo: ProductInfo =
                                                productsInfo?.[selectedKitObject.productNumber];
                                            const product = pointsProducts[selectedKitObject.productNumber];

                                            return (
                                                <StyledWrapper {...styles.completeTableWrapper}>
                                                    <DataTable className="mobile-view" {...styles.dataTable}>
                                                        <TableHeading />
                                                        <DataTableBody>
                                                            <BomTableProductCard
                                                                key={product?.id}
                                                                mainProductNumber={mainProductNumber}
                                                                product={product}
                                                                productInfo={productInfo}
                                                            />
                                                        </DataTableBody>
                                                    </DataTable>
                                                </StyledWrapper>
                                            );
                                        })()}
                                </>
                            )}
                            <StyledWrapper>
                                <Typography {...styles.filterName}>
                                    {selectedKit === "all"
                                        ? translate("All Parts")
                                        : `${siteMessage(
                                              "Custom_Kit_Table_Name",
                                              pointsProducts?.[selectedKit]?.productTitle || selectedKit,
                                          )}`}
                                </Typography>
                                {selectedKit !== "all" && selectedKitObject && (
                                    <StyledWrapper {...styles.tableWrapper}>
                                        <DataTable className="mobile-view" {...styles.dataTable}>
                                            <TableHeading />
                                            <DataTableBody>
                                                {[...selectedKitObject.kitProducts]
                                                    .sort((a, b) => a.sortOrder - b.sortOrder)
                                                    ?.map((kitProduct, index) => {
                                                        if (sortOrders?.[kitProduct.productNumber]) {
                                                            const productInfo: ProductInfo = {
                                                                ...productsInfo?.[kitProduct.productNumber],
                                                                qtyOrdered: kitProduct.qty,
                                                            };
                                                            const product = pointsProducts[kitProduct.productNumber];

                                                            return (
                                                                <BomTableProductCard
                                                                    key={product?.id || index}
                                                                    mainProductNumber={mainProductNumber}
                                                                    product={product}
                                                                    productInfo={productInfo}
                                                                    description={kitProduct.description}
                                                                    productNumber={kitProduct.productNumber}
                                                                    displaySequence={kitProduct.displaySequence}
                                                                />
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                            </DataTableBody>
                                        </DataTable>
                                    </StyledWrapper>
                                )}
                                {selectedKit === "all" && (
                                    <StyledWrapper {...styles.tableWrapper}>
                                        <DataTable className="mobile-view" {...styles.dataTable}>
                                            <TableHeading />
                                            <DataTableBody>
                                                {productsFromBomApi &&
                                                    [...productsFromBomApi]
                                                        ?.sort((a, b) => a.sortOrder - b.sortOrder)
                                                        .map((item, index) => {
                                                            const productInfo: ProductInfo = {
                                                                ...productsInfo?.[item.productNumber],
                                                                qtyOrdered: item.qty,
                                                            };
                                                            const product = pointsProducts[item.productNumber];

                                                            return (
                                                                <BomTableProductCard
                                                                    key={product?.id || index}
                                                                    mainProductNumber={mainProductNumber}
                                                                    product={product}
                                                                    productInfo={productInfo}
                                                                    description={item.description}
                                                                    productNumber={item.productNumber}
                                                                    displaySequence={item.displaySequence}
                                                                />
                                                            );
                                                        })}
                                            </DataTableBody>
                                        </DataTable>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                )}
                {productsFromBomApi && (
                    <>
                        <StyledWrapper
                            {...styles.bottomSection}
                            className={`sticky-element ${isSticky ? "sticky" : ""}`}
                            {...{ alignX: size.x ?? -1 }}
                        >
                            <StyledWrapper {...styles.bottomSectionInnerWrapper}>
                                <StyledWrapper {...styles.IconInfoWrapper}>
                                    <Clickable {...styles.backToTop} onClick={backToTopClickHandler}>
                                        <ButtonIcon color="primary.main" src={IconBackTop} size={36} />
                                    </Clickable>
                                    <StyledWrapper {...styles.InfoWrapper}>
                                        {totalQuantity === 0 && (
                                            <Typography {...styles.selectPiece}>
                                                {translate("Select an Item to Request a Quote")}
                                            </Typography>
                                        )}
                                        <SmallHeadingAndText
                                            heading={translate("Items Selected")}
                                            text={totalQuantity.toLocaleString()}
                                            extendedStyles={styles.smallHeadingAndTextStyle}
                                        />
                                        {isAuthenticated && (
                                            <SmallHeadingAndText
                                                heading={translate("Quote Total")}
                                                text={`${currencySymbol}${totalCost.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                })}`}
                                                extendedStyles={styles.smallHeadingAndTextStyle}
                                            />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.buttonWrapper}>
                                    {BLUEPRINT_NAME === "warthog" && !isAuthenticated
                                        ? createWidgetElement("ProductDetails/FindYourDealer", {
                                              fields: {
                                                  buttonText: translate("Find Your Dealer"),
                                                  displayMessage: false,
                                                  isMultipleProducts: true,
                                              },
                                          })
                                        : contactForQuoteWidgetFields &&
                                          createWidgetElement("ProductDetails/ContactUsForQuoteButton", {
                                              fields: {
                                                  ...contactForQuoteWidgetFields,
                                                  isMultipleProducts: true,
                                                  displayMessage: false,
                                                  buttonText: translate("Request a Quote"),
                                              },
                                          })}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>

                        <StyledWrapper ref={wrapperRef}></StyledWrapper>
                    </>
                )}
                <TwoButtonModal
                    modalIsOpen={confirmationModalIsOpen}
                    headlineText={translate("Change Selected Kit")}
                    messageText={translate("Are you sure you want to change the selected kit?")}
                    cancelButtonText={translate("Cancel")}
                    submitButtonText={translate("Yes")}
                    submitTestSelector="confirmButton"
                    onCancel={cancelClickHandler}
                    onSubmit={confirmClickHandler}
                    extendedStyles={styles.confirmModal}
                ></TwoButtonModal>
                {!productsFromBomApi && !isLoading && (
                    <>
                        <Notify messageType="warning" body={siteMessage("Custom_BOM_No_Products")} />
                    </>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsBomView)),
    definition: {
        displayName: "Product Details BOM View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
